import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import type { SxProps, Theme } from '@mui/material/styles';
import { TablePaginationActionsProps } from '@mui/material/TablePagination/TablePaginationActions';

import useResponsive from '~/hooks/useResponsive';

interface PaginationActionsProps extends TablePaginationActionsProps {
  isDataTable?: boolean;
}

interface PaginationRangeArgs {
  count: number;
  page: number;
  rowsPerPage: number;
  paginationEnabled: boolean;
}

const createButtonSx = (size: number): SxProps<Theme> => ({
  '&.MuiButton-root:not(.MuiButton-text)': {
    minWidth: `${size}px`,
    width: `${size}px`,
    height: `${size}px`,
    borderColor: 'divider',
    backgroundColor: 'background.paper',
  },
});

const getPaginationRange = ({
  count,
  page,
  rowsPerPage,
  paginationEnabled,
}: PaginationRangeArgs) => {
  if (count === 0) {
    return { from: 0, to: 0 };
  }

  if (!paginationEnabled) {
    return { from: 1, to: count };
  }

  const from = Math.min(page * rowsPerPage + 1, count);
  const to = Math.min(from + rowsPerPage - 1, count);

  return { from, to };
};

const iconSx = { fontSize: 16 };

const PaginationActions = ({
  count,
  page,
  rowsPerPage,
  onPageChange,
  isDataTable = false,
}: PaginationActionsProps) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'hostingResourceCard.pagination',
  });
  const { isMobile } = useResponsive();
  const isMobileView = isMobile();
  const isDataTableMobile = isDataTable && isMobileView;
  const size = isDataTable && !isMobileView ? 36 : 32;
  const buttonSx = useMemo(() => createButtonSx(size), [size]);

  const clampedCount = Math.max(count, 0);
  const paginationEnabled = rowsPerPage > 0;
  const totalPages = paginationEnabled
    ? Math.ceil(clampedCount / rowsPerPage)
    : 0;
  const canGoPrevious = paginationEnabled && page > 0;
  const canGoNext =
    paginationEnabled && totalPages > 0 && page < totalPages - 1;

  const { from, to } = useMemo(
    () =>
      getPaginationRange({
        count: clampedCount,
        page,
        rowsPerPage,
        paginationEnabled,
      }),
    [clampedCount, page, paginationEnabled, rowsPerPage],
  );

  const previousButtonOverride = isDataTableMobile
    ? {}
    : {
        borderRightWidth: 0,
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
      };

  const nextButtonOverride = isDataTableMobile
    ? {}
    : {
        borderLeftWidth: 0,
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
      };

  return (
    <Stack
      direction="row"
      alignItems="center"
      ml="auto"
      pl={isDataTableMobile ? 0 : 2}
      flex={isDataTableMobile ? 1 : 0}
      justifyContent="space-between"
    >
      <Button
        variant="outlined"
        onClick={(event) => onPageChange(event, page - 1)}
        disabled={!canGoPrevious}
        aria-label={t('previous')}
        sx={{ ...buttonSx, ...previousButtonOverride }}
        data-testid="pagination-actions-previous"
      >
        <ChevronLeftIcon sx={iconSx} />
      </Button>
      {isDataTableMobile && t('fromToResults', { from, to })}
      <Button
        variant="outlined"
        onClick={(event) => onPageChange(event, page + 1)}
        disabled={!canGoNext}
        aria-label={t('next')}
        sx={{ ...buttonSx, ...nextButtonOverride }}
        data-testid="pagination-actions-next"
      >
        <ChevronRightIcon sx={iconSx} />
      </Button>
    </Stack>
  );
};

export default PaginationActions;
